import { planarTopologyHandleWithAssemblyRegion } from "building-generation/elements/walls2/planarTopologyAssemblyRegionHandle";
import {
  assemblyRegionToSplitRegion,
  computePlanarTopologyAssemblyRegions,
  intersectAssemblyRegionsWithDatumSplits,
} from "building-generation/elements/walls2/planarTopologyAssemblyRegions";
import { pickHandleForPlanarTopologySplitRegion } from "building-generation/elements/walls2/planarTopologyFinishIntegerId";
import type { TopologyPlanUBounds } from "building-generation/elements/walls2/planarTopologySelectionGeometry";
import {
  computePlanarTopologySplitRegionsForDoc,
  type PlanarTopologySplitRegion,
} from "building-generation/elements/walls2/planarTopologySplitRegions";
import { verticalBuildingGraphPlanarTopologyIds } from "building-generation/elements/walls2/wallPlaneTopologyPlanLevelFilter";
import type { Building } from "building-types/building/buildingTypes";
import { applyDatumTagFilterForWallPlane, type DatumTagFilter } from "building-types/datums/datumTagFilterUtils";
import type { Doc } from "building-types/doc";
import type { BuildingGraphPlanarTopologyHandle } from "building-types/entities/buildingEntityHandle";
import type { GeneratedEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import type { PlanarTopologyAssemblySlot } from "building-types/walls2/wallPlaneOperations";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON } from "geom/core";
import { polyLine2AABB2 } from "geom/polyLine2";

/** Fields read when enumerating planar topology split pick regions. */
export interface BuildingForPickRegionEnumeration extends Pick<Building, "buildingGraph" | "wallPlanes"> {
  readonly doc: Pick<Doc, "datums">;
}

export interface PlanarTopologySplitPickRegion {
  readonly topologyId: string;
  readonly splitRegion: PlanarTopologySplitRegion;
  readonly uBounds: TopologyPlanUBounds;
  readonly pickHandle: GeneratedEntityHandle;
  readonly handle: GeneratedEntityHandle;
}

function wallPlaneOutlineAabb(building: BuildingForPickRegionEnumeration, planeHash: string): AABB2 | undefined {
  const buildingPlane = building.buildingGraph.buildingPlanes[planeHash];
  const loop = buildingPlane?.planeFace.loops[0];
  if (!loop) {
    return undefined;
  }
  return polyLine2AABB2(loop);
}

function pickRegionFromSplit(
  topologyId: string,
  splitRegion: PlanarTopologySplitRegion,
  entity: BuildingGraphPlanarTopologyHandle,
): PlanarTopologySplitPickRegion {
  return {
    topologyId,
    splitRegion,
    uBounds: { uMin: splitRegion.uMin, uMax: splitRegion.uMax },
    pickHandle: pickHandleForPlanarTopologySplitRegion(topologyId, splitRegion, entity),
    handle: {
      type: "GENERATED_ENTITY",
      entity,
    },
  };
}

export function enumeratePlanarTopologySplitPickRegionsForTopology(
  building: BuildingForPickRegionEnumeration,
  topologyId: string,
  slot: PlanarTopologyAssemblySlot,
  datumTagFilter?: DatumTagFilter,
  activeStepDatumTag?: string,
  includeShareableVerticalDatums: boolean = true,
): readonly PlanarTopologySplitPickRegion[] {
  const topology = building.buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return [];
  }

  const datums = applyDatumTagFilterForWallPlane(
    building.doc.datums,
    datumTagFilter,
    topology.planeHash,
    activeStepDatumTag,
  );

  // Full-height assembly merge; plan-level filtering belongs with
  // filterPlanarTopologyAssemblyRegionsTouchingPlanLevels (see appendPlanTopologySelectorBoxes).
  const assemblyRegions = computePlanarTopologyAssemblyRegions(building, topologyId, slot);
  if (assemblyRegions.length === 0) {
    return [];
  }

  // Datum splits use full topology UV so multi-level bands keep stable handle identity.
  const datumSplitRegions = computePlanarTopologySplitRegionsForDoc(
    building.doc,
    topology,
    building.buildingGraph,
    undefined,
    datumTagFilter,
    activeStepDatumTag,
    includeShareableVerticalDatums,
  );

  const intersectedRegions = intersectAssemblyRegionsWithDatumSplits(assemblyRegions, datumSplitRegions);
  const planeOutlineAabb = wallPlaneOutlineAabb(building, topology.planeHash);
  if (!planeOutlineAabb) {
    return [];
  }

  const pickRegions: PlanarTopologySplitPickRegion[] = [];
  for (const assemblyRegion of intersectedRegions) {
    const matchingDatumRegion = datumSplitRegions.find(
      (datumRegion) =>
        Math.abs(datumRegion.uMin - assemblyRegion.uMin) <= DIST_EPSILON &&
        Math.abs(datumRegion.uMax - assemblyRegion.uMax) <= DIST_EPSILON &&
        Math.abs(datumRegion.vMin - assemblyRegion.vMin) <= DIST_EPSILON &&
        Math.abs(datumRegion.vMax - assemblyRegion.vMax) <= DIST_EPSILON,
    );
    const splitRegion = assemblyRegionToSplitRegion(assemblyRegion, matchingDatumRegion);
    const entity = planarTopologyHandleWithAssemblyRegion(
      topologyId,
      assemblyRegion,
      planeOutlineAabb,
      topology,
      datums,
    );
    pickRegions.push(pickRegionFromSplit(topologyId, splitRegion, entity));
  }

  return pickRegions;
}

export function enumeratePlanarTopologySplitPickRegions(
  building: BuildingForPickRegionEnumeration,
  slot: PlanarTopologyAssemblySlot,
  topologyIds: readonly string[] = verticalBuildingGraphPlanarTopologyIds(building),
  datumTagFilter?: DatumTagFilter,
  activeStepDatumTag?: string,
  includeShareableVerticalDatums: boolean = true,
): readonly PlanarTopologySplitPickRegion[] {
  const pickRegions: PlanarTopologySplitPickRegion[] = [];
  for (const topologyId of topologyIds) {
    pickRegions.push(
      ...enumeratePlanarTopologySplitPickRegionsForTopology(
        building,
        topologyId,
        slot,
        datumTagFilter,
        activeStepDatumTag,
        includeShareableVerticalDatums,
      ),
    );
  }
  return pickRegions;
}
